import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';
import Footer from './Footer';
import LanguageSwitch from './LanguageSwitch';
import './AuditFunnel.css';

const TOTAL_QUESTIONS = 5;

const ScoreRing = ({ score, maxScore }) => {
  const radius = 54;
  const stroke = 5;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / maxScore) * circumference;
  const percentage = Math.round((score / maxScore) * 100);

  const getColor = () => {
    if (percentage >= 75) return '#4ade80';
    if (percentage >= 45) return '#facc15';
    return '#f87171';
  };

  return (
    <div className="af-score-ring">
      <svg width="128" height="128" viewBox="0 0 128 128">
        <circle
          cx="64" cy="64" r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
        />
        <circle
          cx="64" cy="64" r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          transform="rotate(-90 64 64)"
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      <div className="af-score-ring-value">
        <span className="af-score-ring-num" style={{ color: getColor() }}>{score}</span>
        <span className="af-score-ring-max">/ {maxScore}</span>
      </div>
    </div>
  );
};

const AuditFunnel = () => {
  const { language } = useLanguage();
  const t = translations[language].auditFunnel;
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-5 = questions, 6 = result teaser, 7 = submitted
  const [answers, setAnswers] = useState({});
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const score = useMemo(() => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  }, [answers]);

  const maxScore = TOTAL_QUESTIONS * 3;

  const scoreLevel = useMemo(() => {
    const pct = (score / maxScore) * 100;
    if (pct >= 75) return 'high';
    if (pct >= 45) return 'mid';
    return 'low';
  }, [score, maxScore]);

  const handleAnswer = (questionIdx, value) => {
    setAnswers(prev => ({ ...prev, [questionIdx]: value }));
    setTimeout(() => {
      if (currentStep < TOTAL_QUESTIONS) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentStep(TOTAL_QUESTIONS + 1);
      }
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    setIsSubmitting(true);
    try {
      const db = getDatabase();
      const auditsRef = ref(db, 'audit-leads');
      await push(auditsRef, {
        ...formData,
        answers,
        score,
        maxScore,
        scoreLevel,
        timestamp: serverTimestamp(),
        source: 'audit-funnel',
      });
      setCurrentStep(TOTAL_QUESTIONS + 2);
    } catch (err) {
      console.error('Audit funnel error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressWidth = currentStep === 0
    ? 0
    : currentStep <= TOTAL_QUESTIONS
      ? (currentStep / TOTAL_QUESTIONS) * 100
      : 100;

  return (
    <div className="af-wrapper">
      {/* Nav */}
      <nav className="af-nav">
        <div className="af-nav-inner">
          <div className="af-nav-logo-wrap">
            <img src="../img/bonusclub_logo.webp" alt="BonusClub" className="af-nav-logo" />
            <span className="af-nav-ai-tag">AI</span>
          </div>
          <LanguageSwitch />
          {currentStep === 0 && (
            <button className="af-nav-cta" onClick={() => setCurrentStep(1)}>
              {t.navCta}
            </button>
          )}
        </div>
      </nav>

      {/* Progress Bar */}
      {currentStep >= 1 && currentStep <= TOTAL_QUESTIONS + 1 && (
        <div className="af-progress-bar">
          <div
            className="af-progress-fill"
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      )}

      {/* Step: Intro */}
      {currentStep === 0 && (
        <section className="af-intro">
          <div className="af-intro-glow af-intro-glow-1" />
          <div className="af-intro-glow af-intro-glow-2" />
          <div className="af-intro-inner">
            <span className="af-intro-badge">{t.introBadge}</span>
            <h1 className="af-intro-title">{t.introTitle}</h1>
            <p className="af-intro-subtitle">{t.introSubtitle}</p>

            <button className="af-start-btn" onClick={() => setCurrentStep(1)}>
              {t.startBtn}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

            <div className="af-intro-meta">
              {t.introMeta.map((item, i) => (
                <span key={i} className="af-intro-meta-item">
                  {item}
                  {i < t.introMeta.length - 1 && <span className="af-intro-meta-dot" />}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Steps: Questions */}
      {currentStep >= 1 && currentStep <= TOTAL_QUESTIONS && (
        <section className="af-question-section">
          <div className="af-question-inner">
            <span className="af-question-counter">
              {t.questionLabel} {currentStep} / {TOTAL_QUESTIONS}
            </span>
            <h2 className="af-question-title">{t.questions[currentStep - 1].question}</h2>

            <div className="af-options">
              {t.questions[currentStep - 1].options.map((opt, i) => (
                <button
                  key={i}
                  className={`af-option ${answers[currentStep - 1] === opt.value ? 'af-option-selected' : ''}`}
                  onClick={() => handleAnswer(currentStep - 1, opt.value)}
                >
                  <span className="af-option-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="af-option-text">{opt.label}</span>
                  {answers[currentStep - 1] === opt.value && (
                    <svg className="af-option-check" width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3.5 8.5L6.5 11.5L12.5 4.5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {currentStep > 1 && (
              <button className="af-back-btn" onClick={() => setCurrentStep(currentStep - 1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t.backBtn}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Step: Result Teaser + Email Gate */}
      {currentStep === TOTAL_QUESTIONS + 1 && (
        <section className="af-result-section">
          <div className="af-result-glow" />
          <div className="af-result-inner">
            <div className="af-result-score-col">
              <span className="af-result-label">{t.resultLabel}</span>
              <ScoreRing score={score} maxScore={maxScore} />
              <h2 className="af-result-verdict">{t.verdicts[scoreLevel].title}</h2>
              <p className="af-result-verdict-desc">{t.verdicts[scoreLevel].desc}</p>

              <div className="af-result-teaser-items">
                {t.resultTeaser.map((item, i) => (
                  <div key={i} className="af-result-teaser-item">
                    <div className="af-result-teaser-lock">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="af-result-form-col">
              <div className="af-gate-card">
                <div className="af-gate-glow" />
                <div className="af-gate-header">
                  <span className="af-gate-eyebrow">{t.gateEyebrow}</span>
                  <h3 className="af-gate-title">{t.gateTitle}</h3>
                  <p className="af-gate-desc">{t.gateDesc}</p>
                </div>

                <form onSubmit={handleSubmit} className="af-gate-form">
                  <div className="af-gate-input-group">
                    <label className="af-gate-label">{t.nameLabel}</label>
                    <input
                      type="text"
                      className="af-gate-input"
                      placeholder={t.namePlaceholder}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="af-gate-input-group">
                    <label className="af-gate-label">{t.emailLabel}</label>
                    <input
                      type="email"
                      className="af-gate-input"
                      placeholder={t.emailPlaceholder}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <button type="submit" className="af-gate-submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <span className="af-spinner" />
                    ) : (
                      <>
                        {t.submitBtn}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="af-gate-note">{t.gateNote}</p>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Step: Success / Full Results */}
      {currentStep === TOTAL_QUESTIONS + 2 && (
        <section className="af-success-section">
          <div className="af-success-glow" />
          <div className="af-success-inner">
            <div className="af-success-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 className="af-success-title">{t.successTitle}</h2>
            <p className="af-success-desc">{t.successDesc}</p>

            <ScoreRing score={score} maxScore={maxScore} />

            <div className="af-success-breakdown">
              {t.questions.map((q, i) => (
                <div key={i} className="af-success-row">
                  <span className="af-success-row-q">{q.shortLabel}</span>
                  <div className="af-success-row-bar">
                    <div
                      className="af-success-row-fill"
                      style={{ width: `${((answers[i] || 0) / 3) * 100}%` }}
                    />
                  </div>
                  <span className="af-success-row-score">{answers[i] || 0}/3</span>
                </div>
              ))}
            </div>

            <p className="af-success-cta-text">{t.successCtaText}</p>
            <a href="https://bonusclub.link" target="_blank" rel="noopener noreferrer" className="af-success-cta">
              {t.successCtaBtn}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            </a>
          </div>
        </section>
      )}

      {/* FAQ (only on intro & success) */}
      {(currentStep === 0 || currentStep === TOTAL_QUESTIONS + 2) && (
        <>
          <section className="af-faq">
            <div className="af-faq-inner">
              <h2 className="af-faq-title">{t.faqTitle}</h2>
              <div className="af-faq-list">
                {t.faqs.map((faq, i) => (
                  <div
                    key={i}
                    className={`af-faq-item ${openFaq === i ? 'af-faq-open' : ''}`}
                  >
                    <button
                      className="af-faq-question"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    >
                      <span>{faq.q}</span>
                      <svg
                        className={`af-faq-chevron ${openFaq === i ? 'af-faq-chevron-open' : ''}`}
                        width="16" height="16" viewBox="0 0 16 16"
                        fill="none" stroke="currentColor" strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round"
                      >
                        <path d="M4 6l4 4 4-4" />
                      </svg>
                    </button>
                    <div className="af-faq-answer">
                      <p>{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <Footer />
        </>
      )}
    </div>
  );
};

export default AuditFunnel;
